import { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "@/lib/auth";
import { toast } from "sonner";
import {
  AlertTriangle, Check, ChevronsUpDown, Loader2, MapPin, RotateCw, Send, Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const STATUS_STYLE = {
  enviada: "bg-amber-100 text-amber-900 border-amber-300",
  aceptada: "bg-emerald-100 text-emerald-900 border-emerald-300",
  rechazada: "bg-rose-100 text-rose-900 border-rose-300",
};

const StatusBadge = ({ status }) => {
  if (!status) return null;
  return (
    <span
      className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
        STATUS_STYLE[status] || "bg-zinc-100 text-zinc-700 border-zinc-300"
      }`}
      data-testid={`proposal-status-${status}`}
    >
      {status}
    </span>
  );
};

const shortDate = (iso) =>
  `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)}`;

const busyText = (c) =>
  (c.busy_courses || [])
    .map((b) => `${b.dates.map(shortDate).join(" y ")} · ${b.client_name}`)
    .join(" | ");

const kmText = (c) => {
  if (c.km_one_way === null || c.km_one_way === undefined) return "";
  const km = `${Math.round(c.km_one_way)} km`;
  return c.km_source === "cp" ? `≈ ${km}` : km;
};

const money = (v) =>
  v === null || v === undefined
    ? "—"
    : `${Number(v).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const NearestTable = ({ data, loading, WaButton }) => (
  <>
    <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500">
      Los 3 más cercanos a {data?.course_location || "el curso"}
    </div>
    {loading && !data ? (
      <p className="py-4 text-sm text-zinc-500">Calculando distancias…</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="candidates-table">
          <thead>
            <tr className="border-b-2 border-zinc-200 text-left text-[11px] uppercase tracking-wide text-zinc-500">
              <th className="py-2 pr-3">Instructor</th>
              <th className="py-2 pr-3">Km (ida)</th>
              <th className="py-2 pr-3">Coste estimado</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {(data?.top || []).map((c, idx) => (
              <tr
                key={c.id}
                className={`border-b border-zinc-100 align-middle ${c.busy ? "bg-rose-50" : ""}`}
                data-testid={`candidate-row-${c.id}`}
              >
                <td className="py-2 pr-3">
                  <div className={`font-semibold ${c.busy ? "text-rose-700" : "text-zinc-900"}`}>
                    <span className="mr-1.5 text-zinc-400">{idx + 1}.</span>
                    {c.name}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {c.address || "Sin dirección en su ficha"}
                    {c.phone ? ` · ${c.phone}` : ""}
                  </div>
                  {c.busy && (
                    <div
                      className="mt-1 inline-flex items-center gap-1 rounded-sm border border-rose-300 bg-white px-1.5 py-0.5 text-[11px] font-bold text-rose-700"
                      data-testid={`candidate-busy-${c.id}`}
                      title="Ya tiene otra formación esos días"
                    >
                      <AlertTriangle className="h-3 w-3" />
                      Ocupado: {busyText(c)}
                    </div>
                  )}
                </td>
                <td
                  className="py-2 pr-3 font-mono text-zinc-800"
                  title={c.km_source === "cp" ? "Aproximado por código postal / localidad" : "Por carretera"}
                  data-testid={`candidate-km-${c.id}`}
                >
                  {kmText(c) || "—"}
                </td>
                <td className="py-2 pr-3">
                  <span className="font-semibold text-zinc-900">{money(c.total_cost)}</span>
                  {c.km_total !== null && c.km_total !== undefined && (
                    <span className="block text-xs text-zinc-500">
                      {c.trips} trayectos · {Math.round(c.km_total)} km
                    </span>
                  )}
                </td>
                <td className="py-2 text-right">
                  <WaButton instructor={c} />
                </td>
              </tr>
            ))}
            {!loading && !(data?.top || []).length && (
              <tr>
                <td colSpan={4} className="py-3 text-sm text-zinc-500">
                  No hay instructores activos dados de alta.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {(data?.top || []).some((c) => c.km_source === "cp") && (
          <p className="mt-2 text-[11px] text-zinc-500" data-testid="candidates-approx-note">
            ≈ distancia aproximada por código postal / localidad (no se pudo
            calcular la ruta por la dirección).
          </p>
        )}
      </div>
    )}
  </>
);

export const InstructorAssignment = ({ courseId, onAssigned }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedId, setPickedId] = useState("");

  const load = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const res = await adminApi.get(
        `/admin/courses/${courseId}/instructor-candidates`
      );
      setData(res.data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "No se pudieron calcular los candidatos."
      );
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  const everyone = useMemo(() => data?.all || [], [data]);
  const assigned = !!(data?.instructor_confirmed || data?.instructor_status === "aceptada");
  const assignedName = data?.assigned_instructor_name || data?.proposal_instructor_name || "";
  const picked = useMemo(
    () => everyone.find((i) => i.id === pickedId) || null,
    [everyone, pickedId]
  );

  const release = async () => {
    try {
      await adminApi.post(`/admin/courses/${courseId}/release-instructor`);
      toast.success("Curso liberado: ya puedes proponérselo a otro instructor.");
      await load();
      onAssigned?.();
    } catch (e) {
      toast.error("No se pudo liberar el curso.");
    }
  };

  const sendProposal = async (instructor) => {
    if (!instructor) return;
    if (
      instructor.busy
      && !window.confirm(
        `${instructor.name} ya tiene otra formación esos días `
        + `(${busyText(instructor)}). ¿Le mandas la propuesta igualmente?`
      )
    ) {
      return;
    }
    setSendingId(instructor.id);
    // Se abre la pestaña antes de la llamada para que el navegador no la
    // bloquee por no venir de un clic directo.
    const win = window.open("", "_blank");
    try {
      const res = await adminApi.post(`/admin/courses/${courseId}/proposal`, {
        instructor_id: instructor.id,
      });
      if (win) win.location.href = res.data.wa_url;
      else window.location.href = res.data.wa_url;
      toast.success(`Propuesta preparada para ${instructor.name}.`);
      await load();
    } catch (e) {
      if (win) win.close();
      toast.error(
        e?.response?.data?.detail || "No se pudo preparar la propuesta."
      );
    } finally {
      setSendingId("");
    }
  };

  const WaButton = ({ instructor, variant = "solid" }) => (
    <Button
      type="button"
      size="sm"
      onClick={() => sendProposal(instructor)}
      disabled={!!sendingId || !instructor?.wa_phone}
      title={
        instructor?.wa_phone
          ? "Abrir WhatsApp con la propuesta"
          : "Este instructor no tiene teléfono en su ficha"
      }
      className={
        variant === "solid"
          ? "h-9 bg-[#25D366] text-xs font-bold text-white hover:bg-[#1fb855]"
          : "h-9 border-2 border-[#25D366] bg-white text-xs font-bold text-[#128C7E] hover:bg-[#f0fff5]"
      }
      data-testid={`proposal-send-${instructor?.id}`}
    >
      {sendingId === instructor?.id ? (
        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
      ) : (
        <Send className="mr-1.5 h-3.5 w-3.5" />
      )}
      Enviar propuesta
    </Button>
  );

  return (
    <div
      className="rounded-md border-2 border-zinc-900 bg-white p-4"
      data-testid="instructor-assignment"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex items-center gap-2 text-sm font-bold text-zinc-950">
          <Users className="h-4 w-4" />
          Asignación de instructor
          {assigned && (
            <span
              className="rounded-sm border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[11px] font-bold uppercase text-emerald-900"
              data-testid="course-confirmed-badge"
            >
              Confirmado
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
        {assigned && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={release}
            className="h-8 border border-zinc-300 text-xs font-semibold"
            data-testid="course-release-instructor"
          >
            Liberar curso
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          className="h-8 border border-zinc-300 text-xs"
          data-testid="candidates-refresh"
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCw className="h-3.5 w-3.5" />
          )}
        </Button>
        </div>
      </div>

      {assigned && (
        <div
          className="mb-3 rounded-md border-2 border-emerald-600 bg-emerald-50 px-4 py-3"
          data-testid="course-assigned-banner"
        >
          <div className="text-base font-black uppercase tracking-wide text-emerald-900">
            Curso asignado
          </div>
          {assignedName && (
            <div className="text-sm font-semibold text-emerald-900" data-testid="course-assigned-name">
              {assignedName}
            </div>
          )}
          <p className="mt-1 text-xs text-emerald-900">
            Si surge algún contratiempo, pulsa «Liberar curso» o envía la
            propuesta a otro instructor.
          </p>
        </div>
      )}

      {!assigned && data?.proposal_instructor_name && (
        <p className="mb-3 text-xs text-zinc-600" data-testid="proposal-last">
          Última propuesta: <b>{data.proposal_instructor_name}</b>{" "}
          <StatusBadge status={data.instructor_status} />
        </p>
      )}

      {!assigned && <NearestTable data={data} loading={loading} WaButton={WaButton} />}

      <div className="mt-5 border-t-2 border-dashed border-zinc-200 pt-4">
        <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500">
          Enviar a otro instructor
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                className="h-11 w-full justify-between border-2 border-zinc-300 text-left font-normal sm:w-80"
                data-testid="candidate-picker-trigger"
              >
                <span className="truncate">
                  {picked ? picked.name : "Buscar instructor…"}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[min(92vw,22rem)] p-0" align="start">
              <Command>
                <CommandInput
                  placeholder="Nombre, localidad o teléfono…"
                  data-testid="candidate-picker-search"
                />
                <CommandList>
                  <CommandEmpty>Sin resultados.</CommandEmpty>
                  <CommandGroup>
                    {everyone.map((i) => (
                      <CommandItem
                        key={i.id}
                        value={`${i.name} ${i.address} ${i.phone}`}
                        onSelect={() => {
                          setPickedId(i.id);
                          setPickerOpen(false);
                        }}
                        data-testid={`candidate-option-${i.id}`}
                      >
                        <Check
                          className={`mr-2 h-4 w-4 ${
                            pickedId === i.id ? "opacity-100" : "opacity-0"
                          }`}
                        />
                        <span className="min-w-0">
                          <span
                            className={`block truncate font-medium ${
                              i.busy ? "text-rose-700" : ""
                            }`}
                          >
                            {i.name}
                            {i.busy && (
                              <span
                                className="ml-2 rounded-sm border border-rose-300 px-1 text-[10px] font-bold uppercase text-rose-700"
                                data-testid={`candidate-option-busy-${i.id}`}
                              >
                                ocupado
                              </span>
                            )}
                          </span>
                          <span className="block truncate text-xs text-zinc-500">
                            <MapPin className="mr-1 inline h-3 w-3" />
                            {i.address || "sin dirección"}
                            {i.phone ? ` · ${i.phone}` : ""}
                            {kmText(i) ? ` · ${kmText(i)}` : ""}
                          </span>
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {picked && <WaButton instructor={picked} variant="outline" />}
          {picked?.busy && (
            <span
              className="inline-flex items-center gap-1 text-xs font-bold text-rose-700"
              data-testid="picked-busy-warning"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              Ocupado: {busyText(picked)}
            </span>
          )}
        </div>
      </div>

      {!!(data?.history || []).length && (
        <div className="mt-5 border-t-2 border-dashed border-zinc-200 pt-4">
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500">
            Historial de propuestas
          </div>
          <ul className="space-y-1.5" data-testid="proposal-history">
            {data.history.map((h) => (
              <li
                key={h.id}
                className="flex flex-wrap items-center gap-2 text-xs text-zinc-700"
                data-testid={`proposal-history-${h.id}`}
              >
                <span className="font-semibold text-zinc-900">
                  {h.instructor_name}
                </span>
                <StatusBadge status={h.status} />
                <span className="text-zinc-500">
                  enviada el{" "}
                  {new Date(h.sent_at).toLocaleString("es-ES", {
                    day: "2-digit", month: "2-digit", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                  {h.responded_at
                    ? ` · respondió el ${new Date(h.responded_at).toLocaleString(
                        "es-ES",
                        { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }
                      )}`
                    : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default InstructorAssignment;
